import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { AppSetting, AppSettingStore } from 'src/app/core/storage/stores/app-settings';
import { FeatureFlagService } from '../feature-flags/feature-flag.service';
import { Application } from './application.enum';
type Header = {
    header_value: string,
    header_key: string,
    app_key: string,
}
type AppInformation = {
    headers: Header[],
    claim_app: {allowed_file_types: Array<string>},
    quoting_app: { available_funds: AvailableFunds, allowed_brands: Array<string>},
    permissions: Array<{name: string}>
    training_videos: boolean
}

type AvailableFund = {
    order: number;
    type: string;
    code: string;
};

type AvailableFunds = Array<AvailableFund>;
export type FeatureFlags = Array<{
    name: string;
}>;

/** @deprecated Use {@link FeatureFlagKey} from feature-flag.constant.ts instead. Values here do not match API casing. */
export enum FeatureFlag {
    presaleUnit = 'presale_unit',
    claimsDecom = 'claims_decom',
    trainingVideos = 'training_videos'
}

@Injectable({
    providedIn: 'root'
})
export class AppInformationService {

    private readonly APP_INFO_CACHE_KEY = `app_info_cache:${location.hostname}`;
    private readonly APP_INFO_CACHE_TTL = 604800000; // 7 days in milliseconds

    public headers: BehaviorSubject<Header[]> = new BehaviorSubject<Header[]>([]);
    public quoteAvailableFunds: BehaviorSubject<AvailableFunds> = new BehaviorSubject<AvailableFunds>([]);
    public quoteAllowedBrands: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public featureFlags: BehaviorSubject<FeatureFlags> = new BehaviorSubject<FeatureFlags>([]);
    public trainingVideos: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(private http: HttpClient, private featureFlagService: FeatureFlagService) { }

    public getAppInformation(application: Application): Observable<AppInformation> {
        // Check cached app information first
        const cached = this.getCachedAppInfo(application);
        if (cached) {
            // Apply cached info immediately
            this.applyAppInformation(cached.appInfo);
            console.log('[App Info Cache] Applied cached app info immediately');
            
            if (cached.etag) {
                // Validate in background - will update if changed
                this.validateCacheWithETag(application, cached.appInfo, cached.etag)
                    .subscribe(updatedInfo => {
                        if (updatedInfo !== cached.appInfo) {
                            console.log('[App Info Cache] App info updated from server');
                        }
                    });
                
                // Return cached info immediately
                return of(cached.appInfo);
            }
        }
        
        // No cache or no ETag, fetch fresh (cached info already applied if available)
        return this.fetchAppInformationFromAPI(application);
    }

    /**
     * Get cached app information if available (ignore expiry, ETag handles freshness)
     */
    private getCachedAppInfo(application: Application): { appInfo: AppInformation; etag: string | null } | null {
        try {
            // Remove legacy unscoped key so it is never read again.
            localStorage.removeItem('app_info_cache');

            const cached = localStorage.getItem(this.APP_INFO_CACHE_KEY);
            if (!cached) {
                console.log('[App Info Cache] No cached app info found');
                return null;
            }
            
            const { appInfo, cachedApplication, etag } = JSON.parse(cached);
            const isSameApp = cachedApplication === application;
            
            if (isSameApp) {
                console.log('[App Info Cache] Using cached app info with ETag:', etag);
                return { appInfo, etag: etag || null };
            }
            
            // Clear cache for different application
            console.log('[App Info Cache] Application mismatch, clearing cache');
            localStorage.removeItem(this.APP_INFO_CACHE_KEY);
            return null;
        } catch (error) {
            console.error('[App Info Cache] Error reading cache:', error);
            return null;
        }
    }

    /**
     * Cache app information in sessionStorage
     */
    private cacheAppInfo(appInfo: AppInformation, application: Application, etag?: string | null): void {
        try {
            const cacheData = {
                appInfo,
                timestamp: Date.now(),
                cachedApplication: application,
                etag: etag || null
            };
            localStorage.setItem(this.APP_INFO_CACHE_KEY, JSON.stringify(cacheData));
            console.log('[App Info Cache] Cached app info with ETag:', etag);
        } catch (error) {
            console.warn('[App Info Cache] Failed to cache app information:', error);
        }
    }

    /**
     * Validate cached app info using ETag conditional request
     */
    private validateCacheWithETag(
        application: Application,
        cachedAppInfo: AppInformation,
        etag: string
    ): Observable<AppInformation> {
        let params = new HttpParams();
        params = params.append('application', application);
        const headers = {
            'If-None-Match': etag,
            'Cache-Control': 'no-cache'
        };

        return this.http.get<AppInformation>('api/v1/app/meta', { params, headers, observe: 'response' }).pipe(
            switchMap((response: HttpResponse<AppInformation>) => {
                // Server returned 200 - content changed
                const newEtag = response.headers.get('ETag');
                const appInfo = response.body;
                if (appInfo) {
                    this.applyAppInformation(appInfo);
                    this.cacheAppInfo(appInfo, application, newEtag);
                }
                return of(appInfo!);
            }),
            catchError((error) => {
                // 304 Not Modified - use cached
                if (error?.status === 304) {
                    this.applyAppInformation(cachedAppInfo);
                    // Update timestamp to extend cache
                    this.cacheAppInfo(cachedAppInfo, application, etag);
                    return of(cachedAppInfo);
                }
                // Other errors - fall back to cached
                console.warn('[App Info Cache] ETag validation failed, using cached app info', error);
                this.applyAppInformation(cachedAppInfo);
                return of(cachedAppInfo);
            })
        );
    }

    /**
     * Fetch app information from API without cache
     */
    private fetchAppInformationFromAPI(application: Application): Observable<AppInformation> {
        let params = new HttpParams();
        params = params.append('application', application);
        const headers = {
            'Cache-Control': 'no-cache'
        };
        
        return this.http.get<AppInformation>('api/v1/app/meta', { params, headers, observe: 'response' }).pipe(
            take(1),
            switchMap((response: HttpResponse<AppInformation>) => {
                const etag = response.headers.get('ETag');
                const appInfo = response.body;
                if (appInfo) {
                    this.applyAppInformation(appInfo);
                    this.cacheAppInfo(appInfo, application, etag);
                }
                return of(appInfo!);
            }),
            catchError((error) => {
                console.error('Failed to fetch app information', error);
                return of({
                    headers: [],
                    claim_app: { allowed_file_types: [] },
                    quoting_app: { available_funds: [], allowed_brands: [] },
                    permissions: [],
                    training_videos: false
                });
            })
        );
    }

    /**
     * Apply app information to service state
     */
    private applyAppInformation(info: AppInformation): void {
        this.storeTTL();
        this.headers.next(info.headers);
        this.storeHeaders(info.headers);
        this.storeQuoteConfigurations(info.quoting_app);
        this.storeFeatureFlags(info.permissions);
        this.storeAllowedBrands(info.quoting_app.allowed_brands);
        this.trainingVideos.next(info.training_videos);
        this.featureFlagService.ensureLoaded().subscribe();
    }

    private storeAllowedBrands(brands: string[]){
        this.quoteAllowedBrands.next(brands);
    }

    public clearCache(): void {
        localStorage.removeItem(this.APP_INFO_CACHE_KEY);
        // Also remove the legacy key (without hostname) left from before the scoped key was introduced.
        localStorage.removeItem('app_info_cache');
        this.featureFlags.next([]);
        this.featureFlagService.reset();
    }

    public storeFeatureFlags(flags: FeatureFlags): void
    {
        this.featureFlags.next(flags);
    }

    private storeHeaders(headers: Header[]){
        const headerStore = {};
        headers.forEach( header => {
            headerStore[header.header_key] = header.header_value
        })
        const headersObject = headers.reduce( (acc, header) => {
            let {header_key, header_value} = header
            return {...acc, [header_key]: header_value};
        },{})

        // const test = headers.reduce( (acc, header) => {
        //     let {header_key, header_value, app_key} = header
        //     return {...acc, [app_key]: {...acc[app_key],[header_key]: header_value}  };
        // },{})

        AppSettingStore.getInstance().store(AppSetting.Headers, JSON.stringify(headersObject))
    }

    public get enableTrainingVideos(): boolean {
        return this.trainingVideos.getValue();
    }

    public getHeaders(): Header[]{
        let headers = AppSettingStore.getInstance().headers
        return headers? JSON.parse(headers) : [];
    }

    private getCacheTTL(){
        return new Date().setTime(new Date().getTime() + (1*60*60*1000));
    }

    public get ttl(): number{
        const ttl = AppSettingStore.getInstance().get(AppSetting.TTL)
        return ttl? +ttl : 0;
    }

    private storeTTL(){
        const ttl = this.getCacheTTL();
        AppSettingStore.getInstance().store(AppSetting.TTL,ttl)
        //localStorage.setItem('APP_META_TTL',ttl+'')
    }

    private storeQuoteConfigurations(configuration: {
        available_funds: AvailableFunds
    }): void
    {
        this.quoteAvailableFunds.next(configuration.available_funds);
    }
}
